import React, { useState, useEffect } from "react";
import {
  Download,
  FileText,
  RefreshCw,
  Calendar,
  Receipt,
  CreditCard,
  Sparkles,
  Workflow,
  ChevronLeft,
  ChevronRight,
  Loader,
} from "lucide-react";
import { listInvoices, downloadInvoice } from "../apis/invoice";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [downloadingId, setDownloadingId] = useState(null);
  const [filter, setFilter] = useState("all");
  const limit = 12;

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const loadInvoices = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      const response = await listInvoices(page, limit);
      const data = response.data?.data || {};
      setInvoices(data.invoices || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Error loading invoices:", error);
      if (!silent) toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDownload = async (invoice) => {
    setDownloadingId(invoice.id);
    try {
      await downloadInvoice(invoice.id, `${invoice.invoice_number}.pdf`);
      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice");
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAmount = (amount) => {
    if (!amount || amount === 0) return "Free";
    return `Rs. ${parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "plan":
        return {
          label: "Plan",
          bg: "bg-blue-50",
          text: "text-blue-700",
          border: "border-blue-200",
          icon: CreditCard,
        };
      case "addon":
        return {
          label: "Add-on",
          bg: "bg-purple-50",
          text: "text-purple-700",
          border: "border-purple-200",
          icon: Workflow,
        };
      case "ai_credits":
        return {
          label: "AI Credits",
          bg: "bg-emerald-50",
          text: "text-emerald-700",
          border: "border-emerald-200",
          icon: Sparkles,
        };
      default:
        return {
          label: type || "Other",
          bg: "bg-gray-50",
          text: "text-gray-700",
          border: "border-gray-200",
          icon: FileText,
        };
    }
  };

  const filteredInvoices =
    filter === "all"
      ? invoices
      : invoices.filter((inv) => inv.type === filter);

  const typeStats = {
    plan: invoices.filter((i) => i.type === "plan").length,
    addon: invoices.filter((i) => i.type === "addon").length,
    ai_credits: invoices.filter((i) => i.type === "ai_credits").length,
  };

  return (
    <div className="min-h-screen bg-[#fff] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                <Receipt size={24} className="text-emerald-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
                <p className="text-sm text-gray-600 mt-0.5 font-medium">
                  View and download your purchase invoices
                </p>
              </div>
            </div>
            <motion.button
              onClick={() => loadInvoices(true)}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all shadow-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCw
                size={18}
                className={refreshing ? "animate-spin" : ""}
              />
              Refresh
            </motion.button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">
                    Total Invoices
                  </p>
                  <p className="text-2xl font-bold text-gray-900">{total}</p>
                </div>
                <div className="p-2 bg-gray-100 rounded-xl border border-gray-200">
                  <Receipt size={20} className="text-gray-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">Plans</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {typeStats.plan}
                  </p>
                </div>
                <div className="p-2 bg-blue-50 rounded-xl border border-blue-200">
                  <CreditCard size={20} className="text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">
                    Add-ons
                  </p>
                  <p className="text-2xl font-bold text-purple-600">
                    {typeStats.addon}
                  </p>
                </div>
                <div className="p-2 bg-purple-50 rounded-xl border border-purple-200">
                  <Workflow size={20} className="text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">
                    AI Credits
                  </p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {typeStats.ai_credits}
                  </p>
                </div>
                <div className="p-2 bg-emerald-50 rounded-xl border border-emerald-200">
                  <Sparkles size={20} className="text-emerald-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="bg-white rounded-2xl border border-gray-200 p-2 inline-flex gap-1.5">
            {[
              { key: "all", label: `All (${invoices.length})` },
              { key: "plan", label: `Plans (${typeStats.plan})` },
              { key: "addon", label: `Add-ons (${typeStats.addon})` },
              { key: "ai_credits", label: `AI Credits (${typeStats.ai_credits})` },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-4 py-2.5 text-sm font-bold rounded-xl transition-all ${
                  filter === tab.key
                    ? "text-gray-900 shadow-sm border border-gray-300"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
                style={filter === tab.key ? { backgroundColor: "#eefa94" } : {}}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Invoice Table */}
        {loading ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-2xl mb-4 border border-gray-200">
              <Loader className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
            <p className="text-sm text-gray-600 font-semibold">
              Loading invoices...
            </p>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-200">
              <Receipt className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              No invoices found
            </h3>
            <p className="text-sm text-gray-600 font-medium">
              {filter !== "all"
                ? `No ${filter === "ai_credits" ? "AI Credits" : filter} invoices available`
                : "Purchase a plan or add-on to generate your first invoice"}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-5 py-3.5 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-5 py-3.5 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <AnimatePresence>
                    {filteredInvoices.map((invoice, index) => {
                      const badge = getTypeBadge(invoice.type);
                      const BadgeIcon = badge.icon;
                      return (
                        <motion.tr
                          key={invoice.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03, duration: 0.25 }}
                          className="hover:bg-gray-50 transition"
                        >
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <div className="p-1.5 bg-gray-100 rounded-lg border border-gray-200">
                                <FileText size={14} className="text-gray-600" />
                              </div>
                              <span className="text-sm font-bold text-gray-900">
                                {invoice.invoice_number}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                              <Calendar size={14} className="text-gray-400" />
                              {formatDate(invoice.invoice_date || invoice.created_at)}
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${badge.bg} ${badge.text} ${badge.border}`}
                            >
                              <BadgeIcon size={12} />
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <p className="text-sm text-gray-700 font-medium truncate max-w-[250px]">
                              {invoice.plan_name || "-"}
                            </p>
                          </td>
                          <td className="px-5 py-4 text-right">
                            <span className="text-sm font-bold text-gray-900">
                              {formatAmount(invoice.total)}
                            </span>
                            {invoice.gst_amount > 0 && (
                              <p className="text-xs text-gray-500 font-medium mt-0.5">
                                incl. GST {formatAmount(invoice.gst_amount)}
                              </p>
                            )}
                          </td>
                          <td className="px-5 py-4 text-center">
                            <motion.button
                              onClick={() => handleDownload(invoice)}
                              disabled={downloadingId === invoice.id}
                              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-gray-900 rounded-xl transition-all shadow-sm hover:shadow-md border border-gray-300 disabled:opacity-50"
                              style={{ backgroundColor: "#eefa94" }}
                              whileHover={{ scale: 1.04 }}
                              whileTap={{ scale: 0.96 }}
                            >
                              {downloadingId === invoice.id ? (
                                <Loader
                                  size={14}
                                  className="animate-spin"
                                />
                              ) : (
                                <Download size={14} />
                              )}
                              Download
                            </motion.button>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {filteredInvoices.map((invoice, index) => {
                const badge = getTypeBadge(invoice.type);
                const BadgeIcon = badge.icon;
                return (
                  <motion.div
                    key={invoice.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.04, duration: 0.25 }}
                    className="p-4"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-sm font-bold text-gray-900">
                          {invoice.invoice_number}
                        </p>
                        <p className="text-xs text-gray-500 font-medium mt-0.5">
                          {formatDate(invoice.invoice_date || invoice.created_at)}
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${badge.bg} ${badge.text} ${badge.border}`}
                      >
                        <BadgeIcon size={10} />
                        {badge.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 font-medium mb-2 truncate">
                      {invoice.plan_name || "-"}
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-bold text-gray-900">
                          {formatAmount(invoice.total)}
                        </span>
                        {invoice.gst_amount > 0 && (
                          <span className="text-xs text-gray-500 ml-1.5">
                            (GST {formatAmount(invoice.gst_amount)})
                          </span>
                        )}
                      </div>
                      <motion.button
                        onClick={() => handleDownload(invoice)}
                        disabled={downloadingId === invoice.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-gray-900 rounded-xl transition-all shadow-sm border border-gray-300 disabled:opacity-50"
                        style={{ backgroundColor: "#eefa94" }}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                      >
                        {downloadingId === invoice.id ? (
                          <Loader size={12} className="animate-spin" />
                        ) : (
                          <Download size={12} />
                        )}
                        PDF
                      </motion.button>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-xs text-gray-600 font-medium">
                  Page {page} of {totalPages} ({total} invoices)
                </p>
                <div className="flex items-center gap-1.5">
                  <motion.button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    whileHover={page > 1 ? { scale: 1.05 } : {}}
                    whileTap={page > 1 ? { scale: 0.95 } : {}}
                  >
                    <ChevronLeft size={16} />
                  </motion.button>
                  <motion.button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="p-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    whileHover={page < totalPages ? { scale: 1.05 } : {}}
                    whileTap={page < totalPages ? { scale: 0.95 } : {}}
                  >
                    <ChevronRight size={16} />
                  </motion.button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Invoices;
